import { truncateClass } from "@assets/style/util/cssUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import styleIf from "@assets/style/util/styleIf"
import useShowOnHoverStyles from "@assets/style/util/useShowOnHoverStyles"
import { DiscoChip, DiscoIcon, DiscoIconButton, DiscoLink, DiscoText } from "@disco-ui"
import { useIsMobile } from "@utils/hook/screenSizeHooks"
import classNames from "classnames"
import { capitalize } from "lodash"

interface Props {
  name: string
  url: string
  dateCreated?: string
  pages?: number
  status?: string
  overflow?: React.ReactNode
  subItem?: boolean
  onClick?: () => void
}

export default function TrainingSourceTableItem({
  name,
  url,
  dateCreated,
  pages,
  status,
  overflow,
  subItem = false,
  onClick,
}: Props) {
  const hasItems = Boolean(pages && pages > 1)
  const classes = useStyles({ subItem, hasItems })
  const showOnHoverClasses = useShowOnHoverStyles()
  const isMobile = useIsMobile()

  const emptyColumn = <DiscoText>{"-"}</DiscoText>

  return (
    <div
      className={classes.row}
      {...(onClick && {
        onClick,
      })}
    >
      <div className={classNames(classes.nameCell, showOnHoverClasses.hoverable)}>
        <div className={classes.nameSection}>
          {subItem && (
            <DiscoIcon
              icon={"iconsax.custom-nesting"}
              className={classes.nested}
              height={16}
              width={16}
            />
          )}
          <div className={classes.name}>
            <DiscoText truncateText={1} variant={"body-sm"}>
              {name}
            </DiscoText>
            {renderUrl()}
          </div>
        </div>

        {!isMobile && (
          <div className={showOnHoverClasses.showable}>
            <DiscoIconButton
              to={url}
              linkProps={{
                target: "_blank",
              }}
              classes={{
                root: classes.sourceButton,
              }}
              svgStyles={{
                height: 16,
                width: 16,
              }}
            >
              <DiscoIcon icon={"iconsax.export-2"} />
            </DiscoIconButton>
          </div>
        )}
      </div>

      <div>
        {dateCreated?.length ? (
          <DiscoText variant={"body-xs"} noWrap>
            {dateCreated}
          </DiscoText>
        ) : (
          emptyColumn
        )}
      </div>

      <div>
        {pages ? (
          <DiscoChip label={pages} borderRadius={"default"} marginRight={2} />
        ) : (
          emptyColumn
        )}
      </div>

      <div>
        {status ? (
          <DiscoChip
            borderRadius={"xl"}
            color={status === "success" ? "green" : status === "error" ? "red" : "yellow"}
            textVariant={"body-xs-600"}
            label={capitalize(status)}
            padding={1}
            classes={{
              label: classes.label,
            }}
          />
        ) : (
          emptyColumn
        )}
      </div>

      <div>{overflow}</div>
    </div>
  )

  function renderUrl() {
    if (isMobile)
      return (
        <DiscoLink
          to={url}
          target={"_blank"}
          textVariant={"body-sm"}
          className={truncateClass(1)}
        >
          {url}
        </DiscoLink>
      )

    return (
      <DiscoText truncateText={1} variant={"body-xs"} color={"text.secondary"}>
        {url}
      </DiscoText>
    )
  }
}

interface StyleProps {
  subItem: boolean
  hasItems: boolean
}

const useStyles = makeUseStyles((theme) => ({
  nameCell: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    justifyContent: "space-between",
    minWidth: 0,
    marginRight: theme.spacing(1),
  },
  nameSection: {
    display: "grid",
    gridAutoFlow: "column",
  },
  name: {
    display: "grid",
  },
  row: ({ subItem, hasItems }: StyleProps) => ({
    display: "grid",
    gridTemplateColumns: "1fr 95px 55px 95px 36px",
    padding: theme.spacing(1.5, 1),
    alignItems: "center",
    borderBottom: `1px solid ${theme.palette.divider}`,

    ...styleIf(hasItems, {
      "&:hover": {
        backgroundColor: theme.palette.groovy.neutral[100],
        ...styleIf(!subItem, {
          cursor: "pointer",
        }),
      },
    }),

    ...styleIf(subItem, {
      backgroundColor: theme.palette.groovy.neutral[100],
    }),

    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr 90px 40px 60px 35px",
    },
  }),
  sourceButton: {
    border: `1px solid ${theme.palette.groovy.neutral[300]}`,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1),
  },
  nested: {
    alignSelf: "flex-start",
    marginRight: theme.spacing(0.5),
    color: theme.palette.groovy.neutral[700],
  },
  label: {
    padding: 0,
  },
}))
